import { Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, tap, takeUntil } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ClipboardModule } from '@angular/cdk/clipboard';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { IThemeExecSummary } from '../../../../../../core/models/results.model';
import { getStageColor, STAGE_COLORS, STAGE_BG_COLORS, StageName } from '../../../../../../shared/utils/bracket.utils';
import { StageFirmsDialogComponent } from './stage-firms-dialog/stage-firms-dialog.component';
import { PdfViewerPopupComponent } from 'src/app/shared/components/pdf-viewer-popup/pdf-viewer-popup.component';
import { LoadingProgressComponent, LoadingStage } from 'src/app/shared/components/loading-progress/loading-progress.component';

const SIDENAV_KEY = 'thematic_exec_summary';

const LOADING_STAGES: LoadingStage[] = [
  { text: 'Gathering the patent families…', atSeconds: 0 },
  { text: 'Calculating innovation metrics for the last 10 years…', atSeconds: 8 },
  { text: 'Measuring innovation momentum & acceleration…', atSeconds: 20 },
  { text: 'Locating the theme on the S-curve…', atSeconds: 32 },
];

@Component({
  selector: 'app-theme-exec-summary',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatDialogModule, MatButtonModule, ClipboardModule, LoadingProgressComponent],
  templateUrl: './theme-exec-summary.component.html',
  styleUrl: './theme-exec-summary.component.css',
})
export class ThemeExecSummaryComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() tab_name: string | null = null;
  @Input() query: string | null = null;
  @Input() theme_params: unknown = null;

  @ViewChild('queryDialogTpl') queryDialogTpl!: TemplateRef<unknown>;

  private destroy$ = new Subject<void>();

  readonly LOADING_STAGES = LOADING_STAGES;

  data: IThemeExecSummary | null = null;
  showSpinner = false;
  showDataMissing = false;
  loadingFailed = false;

  readonly stages: { num: number; name: StageName }[] = [
    { num: 1, name: 'Emerging' },
    { num: 2, name: 'Accelerating' },
    { num: 3, name: 'Maturing' },
    { num: 4, name: 'Declining' },
  ];

  // S-curve stage x/y positions on viewBox 600x220
  private readonly stageX = [80, 220, 390, 520];
  private readonly stageY = [158, 100, 38, 20];

  // Full sigmoid path
  readonly curvePath = 'M 30,170 C 60,170 100,165 140,150 C 180,135 200,115 240,90 C 280,65 320,42 390,30 C 440,22 490,18 570,16';

  // Segment boundaries (midpoints between stages for clipping)
  private readonly segBounds = [
    [0, 150],
    [150, 305],
    [305, 455],
    [455, 600],
  ];

  // 2x2 quadrant plot area boundaries
  private readonly QUAD = { left: 75, right: 455, top: 30, bottom: 260 };

  STAGE_COLORS = STAGE_COLORS;
  STAGE_BG_COLORS = STAGE_BG_COLORS;
  getStageColor = getStageColor;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private dialog: MatDialog,
  ) { 
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.reset();
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.data = null;
    this.showSpinner = false;
    this.showDataMissing = false;
    this.loadingFailed = false;
  }

  private loadData(): void {
    const currentTabId = this.tabId;
    this.showSpinner = true;

    const p = (this.theme_params as any)?.params ?? {};

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, SIDENAV_KEY)).pipe(
      take(1),
      switchMap(cached => {
        if (cached) {
          if (this.tabId === currentTabId) {
            this.data = cached;
            this.showSpinner = false;
          }
          return of(null);
        }
        return this.apiService.getThemeExecSummary({
          cluster_id: p.cluster_id ?? null,
          cluster_name: p.cluster_name ?? null,
          theme_search_type: p.theme_search_type ?? 'cluster',
          query: this.query,
          tabId: currentTabId,
        }).pipe(
          takeUntil(this.destroy$),
          tap(res => {
            const payload = res.body?.payload;
            if (!res.ok || !payload?.data) {
              if (this.tabId === currentTabId) {
                this.showDataMissing = true;
                this.showSpinner = false;
              }
              return;
            }
            const targetTabId = payload.tabId ?? currentTabId;
            this.store.dispatch(loadSidenavData({
              tabId: targetTabId,
              sidenavKey: SIDENAV_KEY,
              data: payload.data,
            }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.data = payload.data;
              this.showSpinner = false;
            }
          }),
        );
      }),
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.loadingFailed = true;
          this.showSpinner = false;
        }
      },
    });
  }

  // --- NA stage ---

  get isNaStage(): boolean {
    return this.data?.current_stage == null;
  }

  get noStageWarning(): { title: string; info: string } | null {
    if (!this.isNaStage) return null;
    if (this.data?.no_stage_reason === 'low_size') {
      return {
        title: 'This technology theme is too low in patent activity to be assigned a technology lifecycle stage.',
        info: 'Technology Lifecycle Stage can only be assigned to technologies with more than 1000 inventions in the last 10 years.',
      };
    }
    if (this.data?.no_stage_reason === 'breakpoint') {
      return {
        title: 'This theme has only recently emerged as a Quant IP Technology Cluster and is too new to receive a stage assignment.',
        info: 'Stage assignment on technology clusters takes place once 12 months of data has been collected.',
      };
    }
    return null;
  }

  get canOpenStageDialog(): boolean {
    return !!this.data && !this.isNaStage;
  }

  // --- S-Curve helpers ---

  get currentStageIndex(): number {
    if (!this.data || this.data.current_stage == null) return -1;
    return this.data.current_stage - 1;
  }

  getStageCx(index: number): number {
    return this.stageX[index] ?? 0;
  }

  getStageCy(index: number): number {
    return this.stageY[index] ?? 0;
  }

  getSegClipX(): number {
    if (this.currentStageIndex < 0) return 0;
    return this.segBounds[this.currentStageIndex][0];
  }

  getSegClipWidth(): number {
    if (this.currentStageIndex < 0) return 0;
    const [start, end] = this.segBounds[this.currentStageIndex];
    return end - start;
  }

  get highlightColor(): string {
    if (!this.data || this.isNaStage) return '#9ca3af';
    return getStageColor(this.data.current_stage_name);
  }

  // --- 2x2 Quadrant helpers ---

  getMidX(): number {
    // Both axes are symmetrized around the median, so the median value
    // (= classification threshold) always lands at the SVG center.
    return (this.QUAD.left + this.QUAD.right) / 2;
  }

  getMidY(): number {
    return (this.QUAD.top + this.QUAD.bottom) / 2;
  }

  getDotPosition(): { x: number; y: number } | null {
    if (!this.data || this.isNaStage) return null;
    const { mom_smooth, accel_smooth } = this.data.cluster_stats;
    if (mom_smooth == null || accel_smooth == null) return null;
    return this.projectToQuad(mom_smooth, accel_smooth);
  }

  getPriorYearDotPosition(): { x: number; y: number } | null {
    if (this.isNaStage) return null;
    const py = this.data?.prior_year_stats;
    if (!py || py.mom_smooth == null || py.accel_smooth == null) return null;
    return this.projectToQuad(py.mom_smooth, py.accel_smooth);
  }

  private projectToQuad(mom: number, accel: number): { x: number; y: number } | null {
    const desc = this.data?.descriptive_stats;
    if (!desc) return null;
    // Thresholds: acceleration uses mean, momentum uses median (matches backend flags).
    const xMid = desc.acceleration.mean;
    const yMid = desc.momentum.median;
    const xExtent = Math.max(xMid - desc.acceleration.min, desc.acceleration.max - xMid);
    const yExtent = Math.max(yMid - desc.momentum.min, desc.momentum.max - yMid);
    if (xExtent === 0 || yExtent === 0) return null;
    // Clamp: cluster_stats and descriptive_stats come from different upstream
    // endpoints, so cluster values can fall outside the descriptive range.
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    const xNorm = clamp((accel - xMid + xExtent) / (2 * xExtent));
    const yNorm = clamp((mom - yMid + yExtent) / (2 * yExtent));
    // Inset by dot radius + stroke so clamped dots sit fully inside the quadrant.
    const pad = 10;
    return {
      x: this.QUAD.left + pad + xNorm * (this.QUAD.right - this.QUAD.left - 2 * pad),
      y: this.QUAD.bottom - pad - yNorm * (this.QUAD.bottom - this.QUAD.top - 2 * pad),
    };
  }

  getQuadrantLabelPos(quadrant: 'TL' | 'TR' | 'BL' | 'BR'): { x: number; y: number } {
    const mx = this.getMidX();
    const my = this.getMidY();
    switch (quadrant) {
      case 'TL': return { x: (this.QUAD.left + mx) / 2, y: (this.QUAD.top + my) / 2 };
      case 'TR': return { x: (mx + this.QUAD.right) / 2, y: (this.QUAD.top + my) / 2 };
      case 'BL': return { x: (this.QUAD.left + mx) / 2, y: (my + this.QUAD.bottom) / 2 };
      case 'BR': return { x: (mx + this.QUAD.right) / 2, y: (my + this.QUAD.bottom) / 2 };
    }
  }

  // --- Derived bracket labels ---

  get momentumLabel(): string {
    if (this.isNaStage) return '-';
    const hm = this.data?.cluster_stats.high_mom;
    return hm == null ? '-' : (hm ? 'High' : 'Low');
  }

  get accelerationLabel(): string {
    if (this.isNaStage) return '-';
    const pa = this.data?.cluster_stats.pos_acc;
    return pa == null ? '-' : (pa ? 'High' : 'Low');
  }

  // --- Table formatting ---

  formatNumber(val: number | null | undefined): string {
    if (val == null) return '-';
    return val.toLocaleString();
  }

  formatPct(val: number | null | undefined): string {
    if (val == null) return '-';
    return (val >= 0.05 ? '+' : '') + val.toFixed(1) + '%';
  }

  formatDecimal(val: number | null | undefined): string {
    if (val == null) return '-';
    return val.toFixed(2);
  }

  // --- Dialogs ---

  openStageCompaniesDialog(): void {
    if (!this.data || !this.canOpenStageDialog) return;
    this.dialog.open(StageFirmsDialogComponent, {
      data: { stage: this.data.current_stage, stage_name: this.data.current_stage_name, tabId: this.tabId },
      width: '70vw',
      maxWidth: '900px',
      maxHeight: '80vh',
    });
  }

  get isCustomTheme(): boolean {
    return ((this.theme_params as any)?.params?.theme_search_type ?? 'cluster') === 'custom';
  }

  openHeaderInfo(): void {
    if (this.isCustomTheme) {
      this.dialog.open(this.queryDialogTpl, { width: '600px', maxWidth: '90vw' });
    } else {
      this.openInfoPopup('theme_clusters');
    }
  }

  openInfoPopup(activeKey: 'theme_clusters' | 'theme_stages' | 'theme_backtest'): void {
    this.dialog.open(PdfViewerPopupComponent, {
      data: {
        tabs: [
          { label: 'Quant IP Clusters', key: 'theme_clusters' },
          { label: 'Technology Stages', key: 'theme_stages' },
          { label: 'Thematic Backtest', key: 'theme_backtest' },
        ],
        activeKey,
      },
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      panelClass: 'pdf-viewer-dialog-panel',
    });
  }
}
